import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock3, LogIn, LogOut, Plus, Settings2, Wallet } from "lucide-react";
import { ModelOption } from "../constants/models";
import { DebateCredits, WeeklyUsage, WEEKLY_FREE_DEBATE_LIMIT, WEEKLY_AUDIO_DEBATE_LIMIT, GUEST_WEEKLY_FREE_DEBATE_LIMIT } from "../firebase/credits";
import { DailyCost } from "../firebase/costs";
import WorkspaceNavigation, { Brand } from "./WorkspaceNavigation";
import Dialog from "./Dialog";
import ThemeToggle from "./ThemeToggle";

interface ChatHeaderProps {
  currentUser: { displayName?: string | null; email?: string | null } | null;
  isGuest: boolean;
  onLogout: () => void;
  onSignIn: () => void;
  councilMode: boolean;
  onToggleCouncilMode: () => void;
  selectedModel: string;
  onSelectModel: (model: string) => void;
  modelOptions: ModelOption[];
  messages: any[];
  onNewChat: () => void;
  credits: DebateCredits;
  onBuyCredits: () => void;
  onOpenHistory: () => void;
  onOpenVideo: () => void;
  usage: WeeklyUsage;
  todayCost: DailyCost;
  isPrivileged: boolean;
}

export default function ChatHeader({ currentUser, onLogout, onSignIn, councilMode, onToggleCouncilMode,
  messages, onNewChat, credits, onBuyCredits, onOpenHistory, onOpenVideo, usage, todayCost, isPrivileged }: ChatHeaderProps) {
  const [showAccount, setShowAccount] = useState(false);
  const navigate = useNavigate();
  const freeLimit = currentUser ? WEEKLY_FREE_DEBATE_LIMIT : GUEST_WEEKLY_FREE_DEBATE_LIMIT;
  const freeLeft = Math.max(0, freeLimit - usage.freeDebates);
  const audioLeft = Math.max(0, WEEKLY_AUDIO_DEBATE_LIMIT - usage.audioDebates);
  return (
    <>
      <header className="workspace-header">
        <Brand />
        <WorkspaceNavigation active={councilMode ? "council" : "chat"} onChange={mode => {
          if (mode === "video") onOpenVideo();
          else if (mode === "feed") navigate("/feed");
          else if ((mode === "council") !== councilMode) onToggleCouncilMode();
        }} />
        <div className="header-actions">
          {!councilMode && messages.length > 0 && <button type="button" className="icon-button" onClick={onNewChat} aria-label="New chat"><Plus size={19} /></button>}
          <button type="button" className="icon-button" onClick={onOpenHistory} aria-label="Open history" title="History"><Clock3 size={19} /></button>
          <button type="button" className="icon-button account-button" aria-label="Account settings" onClick={() => setShowAccount(true)}><Settings2 size={18} /></button>
          {!currentUser && <button type="button" className="button-primary header-signin" onClick={onSignIn}><LogIn size={16} /><span>Sign in</span></button>}
        </div>
      </header>
      <Dialog open={showAccount} onClose={() => setShowAccount(false)} title="Settings">
        <div className="account-summary"><span className="eyebrow">{currentUser ? "SIGNED IN" : "GUEST WORKSPACE"}</span>
          <h3>{currentUser?.displayName || "Guest"}</h3>
          <p>{currentUser?.email || "Sign in for history, audio, and video."}</p>
        </div>
        <ThemeToggle />
        <div className="usage-card"><div><span>Free debates</span><strong>{freeLeft} / {freeLimit} left</strong></div><progress aria-label="Free debates remaining" max={freeLimit} value={freeLeft} /><small>Resets every Monday</small></div>
        {currentUser && <>
          <div className="usage-card"><div><span>Audio debates</span><strong>{audioLeft} / {WEEKLY_AUDIO_DEBATE_LIMIT} left</strong></div><progress aria-label="Audio debates remaining" max={WEEKLY_AUDIO_DEBATE_LIMIT} value={audioLeft} /></div>
          <div className="account-balances"><div><small>Plus debates</small><strong>{credits.plusCredits}</strong></div><div><small>Pro debates</small><strong>{credits.proCredits}</strong></div><div><small>Chat & video</small><strong>{isPrivileged ? "Included" : `$${(credits.chatCredits ?? 0).toFixed(2)}`}</strong></div></div>
          {todayCost.total > 0 && <div className="account-cost"><span>Today's API cost</span><strong>${todayCost.total.toFixed(4)}</strong><small>Chat ${todayCost.chat.toFixed(4)} · Debates ${todayCost.debate.toFixed(4)}</small></div>}
          {!isPrivileged && <button type="button" className="button-primary w-full" onClick={() => { setShowAccount(false); onBuyCredits(); }}><Wallet size={17} />Add credits</button>}
          <button type="button" className="button-secondary w-full" onClick={() => { setShowAccount(false); onLogout(); }}><LogOut size={16} />Sign out</button>
        </>}
        {!currentUser && <button type="button" className="button-primary w-full" onClick={() => { setShowAccount(false); onSignIn(); }}><LogIn size={17} />Sign in with Google</button>}
      </Dialog>
    </>
  );
}
